import { create } from 'zustand'

import { Asset } from '@/lib/features/Assets/interfaces'

import { AssetTag, ModalOpenVariant } from '../components/TableHeaderTags/TableHeaderTags.types'
import { TagsState } from './tagsStore.types'

export const useTagsStore = create<TagsState>((set, get) => ({
  globalOrgTags: [],
  setGlobalOrgTags: (tags: AssetTag[]) => set({ globalOrgTags: tags }),

  removeTagFromGlobalState: (tagId: number) => {
    const state = get()
    const filteredTags = state.globalOrgTags.filter((tag) => tag.id !== tagId)
    set({
      globalOrgTags: filteredTags,
    })
  },

  updateTagInGlobalState: (tagId: number, updatedTag: AssetTag) => {
    const state = get()
    const updatedTags = state.globalOrgTags.map((tag) =>
      tag.id === tagId ? { ...tag, ...updatedTag } : tag,
    )
    set({ globalOrgTags: updatedTags })
  },

  activeAssetData: undefined,
  setActiveAssetData: (assetData: Asset) => set({ activeAssetData: assetData }),

  // Tag operations loading state
  isTagsUpdating: false,
  setTagsUpdating: (isUpdating) => set({ isTagsUpdating: isUpdating }),

  // Popout state
  isPopoutOpen: false,
  popoverPosition: null,
  activeAssetId: undefined,
  activeAssetTagList: [],
  openPopout: ({ position, activeAssetData }) => {
    set({
      isPopoutOpen: true,
      popoverPosition: position,
      activeAssetData: activeAssetData,
      // Make sure modal is closed when popout is opened
      isModalOpen: false,
    })
  },
  closePopout: () =>
    set({
      isPopoutOpen: false,
      activeAssetData: undefined,
      popoverPosition: null,
    }),
  togglePopout: ({ position, activeAssetData }) => {
    set((state) => {
      // If popout is open for the same asset, close it
      if (state.isPopoutOpen && state.activeAssetData?.id === activeAssetData?.id) {
        return {
          isPopoutOpen: false,
          popoverPosition: null,
          activeAssetId: undefined,
          activeAssetTagList: [],
        }
      }

      // Otherwise, open popout for this asset (or switch to this asset)
      return {
        isPopoutOpen: true,
        popoverPosition: position,
        activeAssetData: activeAssetData,
        // Make sure modal is closed when popout is opened
        isModalOpen: false,
      }
    })
  },

  // Add filter popout state
  isFilterPopoutOpen: false,
  filterPopoverPosition: null,
  openFilterPopout: ({ position }) => {
    set({
      isFilterPopoutOpen: true,
      filterPopoverPosition: position,
    })
  },
  closeFilterPopout: () =>
    set({
      isFilterPopoutOpen: false,
      filterPopoverPosition: null,
    }),
  toggleFilterPopout: ({ position }) => {
    set((state) => {
      if (state.isFilterPopoutOpen) {
        return {
          isFilterPopoutOpen: false,
          filterPopoverPosition: null,
        }
      }

      return {
        isFilterPopoutOpen: true,
        filterPopoverPosition: position,
      }
    })
  },
  filteringTags: [],
  setFilteringTags: (tags) => set({ filteringTags: tags || [] }),

  isGeofenceTagsPopoutOpen: false,
  geofenceTagsPopoverPosition: null,
  openGeofenceTagsPopout: ({ position }) => {
    set({
      isGeofenceTagsPopoutOpen: true,
      geofenceTagsPopoverPosition: position,
    })
  },
  closeGeofenceTagsPopout: () =>
    set({
      isGeofenceTagsPopoutOpen: false,
      geofenceTagsPopoverPosition: null,
    }),
  toggleGeofenceTagsPopout: ({ position }) => {
    set((state) => {
      if (state.isGeofenceTagsPopoutOpen) {
        return {
          isGeofenceTagsPopoutOpen: false,
          geofenceTagsPopoverPosition: null,
        }
      }

      return {
        isGeofenceTagsPopoutOpen: true,
        geofenceTagsPopoverPosition: position,
      }
    })
  },
  geofenceAlertTags: [],
  setGeofenceAlertTags: (tags) => set({ geofenceAlertTags: tags || [] }),

  // Alert tags popout state
  isAlertTagsPopoutOpen: false,
  alertTagsPopoverPosition: null,
  openAlertTagsPopout: ({ position }) => {
    set({
      isAlertTagsPopoutOpen: true,
      alertTagsPopoverPosition: position,
    })
  },
  closeAlertTagsPopout: () =>
    set({
      isAlertTagsPopoutOpen: false,
      alertTagsPopoverPosition: null,
    }),
  toggleAlertTagsPopout: ({ position }) => {
    set((state) => {
      if (state.isAlertTagsPopoutOpen) {
        return {
          isAlertTagsPopoutOpen: false,
          alertTagsPopoverPosition: null,
        }
      }

      return {
        isAlertTagsPopoutOpen: true,
        alertTagsPopoverPosition: position,
      }
    })
  },
  alertTags: [],
  setAlertTags: (tags) => set({ alertTags: tags || [] }),

  // Bulk tags modal state
  isBulkTagsModalOpen: false,
  bulkTagsSelectedAssets: [],
  bulkTagsOnSuccess: null,
  openBulkTagsModal: (assets, onSuccess) =>
    set({ isBulkTagsModalOpen: true, bulkTagsSelectedAssets: assets, bulkTagsOnSuccess: onSuccess ?? null }),
  closeBulkTagsModal: () =>
    set({ isBulkTagsModalOpen: false, bulkTagsSelectedAssets: [], bulkTagsOnSuccess: null }),

  // Modal state
  isModalOpen: false,
  modalVariant: ModalOpenVariant.ASSET,
  openModal: (variant = ModalOpenVariant.ASSET) => {
    set((state) => {
      if (variant === ModalOpenVariant.FILTER && state.isPopoutOpen) {
        return {
          isModalOpen: true,
          modalVariant: variant,
          isPopoutOpen: false,
          activeAssetId: undefined,
        }
      }
      return { isModalOpen: true, modalVariant: variant }
    })
  },
  closeModal: () => set({ isModalOpen: false }),
  toggleModal: (variant = ModalOpenVariant.ASSET) => {
    set((state) => {
      const newState = !state.isModalOpen
      return {
        isModalOpen: newState,
        modalVariant: newState ? variant : state.modalVariant,
      }
    })
  },
}))
